import React, { Suspense, lazy } from 'react'
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './AuthContext'
import { useInactivityTimeout } from './hooks/useInactivityTimeout'
import { ThemeProvider, useTheme } from './ThemeContext'
import { LayoutProvider, useLayout } from './LayoutContext'
import { useOrganization } from './hooks/useOrganization'
import { supabase } from './supabaseClient'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingFallback from './components/LoadingFallback'
import Login from './components/Login'
import LarcenyReportForm from './components/LarcenyReportForm'
import MissionLanding from './components/MissionLanding'
import MissionTally from './components/MissionTally'
import OrganizationSelector from './components/OrganizationSelector'
import Establishments from './components/Establishments'
import Dashboard from './components/Dashboard'
import AgentDashboard from './components/AgentDashboard'
import ConfirmDialog from './components/ConfirmDialog'
import ModernLayout from './components/ModernLayout'
import SMSConsentGate from './components/SMSConsentGate'
import './App.css'

// Lazy load heavy components that aren't needed immediately
const InspectionForm = lazy(() => import('./components/InspectionForm'))
const AdminDashboard = lazy(() => import('./components/AdminDashboard'))
const SuperAdminDashboard = lazy(() => import('./components/SuperAdminDashboard'))
const NewOpeningAssistant = lazy(() => import('./components/NewOpeningAssistant'))
const ComponentShowcase = lazy(() => import('./components/ComponentShowcase'))
const DailyActivityLog = lazy(() => import('./components/DailyActivityLog'))
const OnCallManagement = lazy(() => import('./components/OnCallManagement'))
const MonthlyReport = lazy(() => import('./components/MonthlyReport'))
const Complaints = lazy(() => import('./components/Complaints'))
const Trespass = lazy(() => import('./components/Trespass'))
const MinorCampaign = lazy(() => import('./components/MinorCampaign'))
const Scheduling = lazy(() => import('./components/Scheduling'))
const ABCLawChat = lazy(() => import('./components/ABCLawChat'))
const AgentNotes = lazy(() => import('./components/AgentNotes'))
const ContactDirectory = lazy(() => import('./components/ContactDirectory'))
const TillDetective = lazy(() => import('./components/TillDetective'))
const LarcenyReports = lazy(() => import('./components/LarcenyReports'))
const PaymentReceipts = lazy(() => import('./components/PaymentReceipts'))
const EvidenceModule = lazy(() => import('./components/EvidenceModule'))
const HelpCenter = lazy(() => import('./components/HelpCenter'))
const RaspClass = lazy(() => import('./components/RaspClass'))

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data is fresh for 5 minutes
      cacheTime: 1000 * 60 * 30, // Cache persists for 30 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Only retry failed requests once
    },
  },
})

function AppContent() {
  const { user, signOut, loading } = useAuth()
  useTheme()
  useLayout()
  const { organization, isSuperAdmin, selectOrganization, clearOrganization, loading: orgLoading } = useOrganization()
  const navigate = useNavigate()
  const location = useLocation()
  const [showInspectionForm, setShowInspectionForm] = React.useState(false)
  const [selectedEstablishmentForInspection, setSelectedEstablishmentForInspection] = React.useState(null)
  const [refreshTrigger, setRefreshTrigger] = React.useState(0)
  const [userRole, setUserRole] = React.useState(null)
  const [establishmentsFilterHandler, setEstablishmentsFilterHandler] = React.useState(null)
  const [currentAgentFilter, setCurrentAgentFilter] = React.useState('')
  const [confirmDialog, setConfirmDialog] = React.useState({ isOpen: false, data: null })
  const [adminKey, setAdminKey] = React.useState(0)
  const [smsConsent, setSmsConsent] = React.useState(null)
  const [existingPhone, setExistingPhone] = React.useState('')
  const [showInactivityWarning, setShowInactivityWarning] = React.useState(false)

  useInactivityTimeout({
    enabled: !!user,
    onWarning: () => setShowInactivityWarning(true),
    onActivity: () => setShowInactivityWarning(false),
    onTimeout: async () => {
      setShowInactivityWarning(false)
      await signOut()
    },
  })

  // Determine current view from URL
  const currentView = location.pathname === '/admin' ? 'admin'
    : location.pathname === '/new-opening' ? 'new-opening'
    : location.pathname === '/daily-log' ? 'daily-log'
    : location.pathname === '/on-call' ? 'on-call'
    : location.pathname === '/monthly' ? 'monthly'
    : location.pathname === '/complaints' ? 'complaints'
    : location.pathname === '/trespass' ? 'trespass'
    : location.pathname === '/minor-campaign' ? 'minor-campaign'
    : location.pathname === '/scheduling' ? 'scheduling'
    : location.pathname === '/abc-law' ? 'abc-law'
    : location.pathname === '/notes' ? 'notes'
    : location.pathname === '/contacts' ? 'contacts'
    : location.pathname === '/larceny' ? 'larceny'
    : location.pathname === '/till-detective' ? 'till-detective'
    : location.pathname === '/payments' ? 'payments'
    : location.pathname === '/evidence' ? 'evidence'
    : location.pathname === '/rasp' ? 'rasp'
    : location.pathname === '/showcase' ? 'showcase'
    : location.pathname === '/help' ? 'help'
    : location.pathname === '/super-admin' ? 'super-admin'
    : location.pathname === '/inspections' ? 'main'
    : 'dashboard'

  // Check user role, agent filter, and SMS consent on mount
  React.useEffect(() => {
    async function checkUserRole() {
      if (!user) return

      try {
        const { data } = await supabase
          .from('users')
          .select('role, first_name, last_name, sms_consent, phone_number')
          .eq('id', user.id)
          .single()

        setUserRole(data?.role)
        if (data?.role === 'inspector') {
          setCurrentAgentFilter(`${data.first_name} ${data.last_name}`)
        }
        const consented = data?.sms_consent === true
        if (consented) localStorage.setItem(`sms_consent_${user.id}`, '1')
        setSmsConsent(consented || localStorage.getItem(`sms_consent_${user.id}`) === '1')
        setExistingPhone(data?.phone_number || '')
      } catch (error) {
        console.error('Error checking user role:', error)
        setSmsConsent(true) // fail open so a DB error doesn't lock users out
      }
    }

    checkUserRole()
  }, [user])

  // Redirect AI-only orgs away from dashboard on login
  React.useEffect(() => {
    if (!organization) return
    if (location.pathname !== '/') return
    if (!organization.features?.core_inspection && organization.features?.abc_law) {
      navigate('/abc-law', { replace: true })
    } else if (window.innerWidth <= 768) {
      navigate('/inspections', { replace: true })
    }
  }, [organization, location.pathname, navigate])

  const handleNewInspection = (establishment = null) => {
    setSelectedEstablishmentForInspection(establishment)
    setShowInspectionForm(true)
  }

  const handleInspectionClose = () => {
    setShowInspectionForm(false)
    setSelectedEstablishmentForInspection(null)
  }

  const handleInspectionSuccess = () => {
    setShowInspectionForm(false)
    setSelectedEstablishmentForInspection(null)
    setRefreshTrigger(prev => prev + 1)
  }

  const handleEstablishmentCreated = () => {
    // Invalidate React Query cache and increment refresh trigger
    queryClient.invalidateQueries()
    setRefreshTrigger(prev => prev + 1)
  }

  const handleViewChange = (view) => {
    if (view === 'dashboard') {
      navigate('/')
    } else if (view === 'main') {
      navigate('/inspections')
      setRefreshTrigger(prev => prev + 1)
    } else if (view === 'admin') {
      navigate('/admin')
      setAdminKey(prev => prev + 1)
    } else if (view === 'new-opening') {
      navigate('/new-opening')
    } else if (view === 'daily-log') {
      navigate('/daily-log')
    } else if (view === 'on-call') {
      navigate('/on-call')
    } else if (view === 'monthly') {
      navigate('/monthly')
    } else if (view === 'complaints') {
      navigate('/complaints')
    } else if (view === 'trespass') {
      navigate('/trespass')
    } else if (view === 'minor-campaign') {
      navigate('/minor-campaign')
    } else if (view === 'scheduling') {
      navigate('/scheduling')
    } else if (view === 'abc-law') {
      navigate('/abc-law')
    } else if (view === 'notes') {
      navigate('/notes')
    } else if (view === 'contacts') {
      navigate('/contacts')
    } else if (view === 'larceny') {
      navigate('/larceny')
    } else if (view === 'till-detective') {
      navigate('/till-detective')
    } else if (view === 'payments') {
      navigate('/payments')
    } else if (view === 'evidence') {
      navigate('/evidence')
    } else if (view === 'rasp') {
      navigate('/rasp')
    } else if (view === 'showcase') {
      navigate('/showcase')
    } else if (view === 'help') {
      navigate('/help')
    } else if (view === 'super-admin') {
      navigate('/super-admin')
    }
  }

  const handleStatusFilterClick = (status) => {
    if (establishmentsFilterHandler?.setStatusFilter) {
      establishmentsFilterHandler.setStatusFilter(status)
    }
  }

  if (loading || orgLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    )
  }

  // Public token-based route — no auth required
  if (location.pathname.startsWith('/report/')) {
    const token = location.pathname.replace('/report/', '').split('/')[0]
    return <LarcenyReportForm token={token} />
  }

  // Public mission tally routes — no auth required
  if (location.pathname === '/mission') {
    return <MissionLanding />
  }
  if (location.pathname.startsWith('/mission/')) {
    return <MissionTally />
  }

  if (!user) {
    return <Login />
  }

  // Super admin needs to select an organization first (except for the super-admin route itself)
  if (isSuperAdmin && !organization && currentView !== 'super-admin') {
    return (
      <OrganizationSelector
        onSelectOrganization={(org) => {
          selectOrganization(org)
          setRefreshTrigger(prev => prev + 1)
        }}
      />
    )
  }

  // Shared sign out handler
  const handleSignOut = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    console.log('Sign out button clicked')
    try {
      clearOrganization()
      await signOut()
      console.log('Sign out successful')
    } catch (error) {
      console.error('Sign out error:', error)
      if (error.message?.includes('Auth session missing')) {
        clearOrganization()
        window.location.reload()
      }
    }
  }

  const handleSwitchOrganization = () => {
    if (!organization) {
      clearOrganization()
      return
    }
    setConfirmDialog({
      isOpen: true,
      data: {
        title: 'Switch Organization',
        message: `Switch from ${organization.name}?`,
        confirmText: 'Switch',
        onConfirm: () => {
          setConfirmDialog({ isOpen: false, data: null })
          clearOrganization()
          setRefreshTrigger(prev => prev + 1)
        }
      }
    })
  }

  // Main content renderer (same for both layouts)
  const renderMainContent = () => (
    <>
      {userRole === 'store' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Trespass List. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Trespass List..." />}>
            <Trespass key={`trespass-store-${refreshTrigger}`} readOnly />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'admin' ? (
        <ErrorBoundary fallbackMessage="There was an error loading the admin dashboard. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Admin Dashboard..." />}>
            <AdminDashboard key={`admin-${adminKey}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'super-admin' && (isSuperAdmin || user?.email === 'josh@signalthree.com') ? (
        <ErrorBoundary fallbackMessage="There was an error loading the Super Admin dashboard.">
          <Suspense fallback={<LoadingFallback message="Loading..." />}>
            <SuperAdminDashboard />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'new-opening' ? (
        <ErrorBoundary fallbackMessage="There was an error loading the New Opening Assistant. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading New Opening Assistant..." />}>
            <NewOpeningAssistant
              onEstablishmentCreated={handleEstablishmentCreated}
            />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'daily-log' ? (
        <ErrorBoundary fallbackMessage="There was an error loading the Daily Activity Log. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Daily Activity Log..." />}>
            <DailyActivityLog key={`daily-log-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'on-call' ? (
        <ErrorBoundary fallbackMessage="There was an error loading On-Call Management. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading On-Call Management..." />}>
            <OnCallManagement key={`on-call-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'monthly' ? (
        <ErrorBoundary fallbackMessage="There was an error loading the Monthly Report form. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Monthly Report..." />}>
            <MonthlyReport key={`monthly-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'complaints' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Complaints. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Complaints..." />}>
            <Complaints key={`complaints-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'trespass' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Trespass List. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Trespass List..." />}>
            <Trespass key={`trespass-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'minor-campaign' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Minor Campaign. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Minor Campaign..." />}>
            <MinorCampaign key={`minor-campaign-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'scheduling' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Scheduling. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Scheduling..." />}>
            <Scheduling key={`scheduling-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'abc-law' ? (
        <ErrorBoundary fallbackMessage="There was an error loading ABC Law Assistant. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading ABC Law Assistant..." />}>
            <ABCLawChat />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'notes' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Notes. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Notes..." />}>
            <AgentNotes key={`notes-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'contacts' && organization?.features?.contact_directory && userRole !== 'store' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Contact Info. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Contact Info..." />}>
            <ContactDirectory key={`contacts-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'till-detective' && organization?.features?.till_detective && (userRole === 'admin' || userRole === 'super_admin') ? (
        <ErrorBoundary fallbackMessage="There was an error loading Till Detective. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Till Detective..." />}>
            <TillDetective key={`till-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'larceny' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Larceny Reports. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Larceny Reports..." />}>
            <LarcenyReports key={`larceny-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'payments' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Payment Receipts. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Payment Receipts..." />}>
            <PaymentReceipts key={`payments-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'evidence' ? (
        <ErrorBoundary fallbackMessage="There was an error loading Evidence. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Evidence..." />}>
            <EvidenceModule key={`evidence-${refreshTrigger}-${currentView}`} organization={organization} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'rasp' ? (
        <ErrorBoundary fallbackMessage="There was an error loading RASP Class. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading RASP Class..." />}>
            <RaspClass key={`rasp-${refreshTrigger}-${currentView}`} />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'showcase' ? (
        <ErrorBoundary fallbackMessage="There was an error loading the component showcase. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Component Showcase..." />}>
            <ComponentShowcase />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'help' ? (
        <ErrorBoundary fallbackMessage="There was an error loading the Help Center. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Help Center..." />}>
            <HelpCenter />
          </Suspense>
        </ErrorBoundary>
      ) : showInspectionForm ? (
        <ErrorBoundary fallbackMessage="There was an error loading the inspection form. Please try again.">
          <Suspense fallback={<LoadingFallback message="Loading Inspection Form..." />}>
            <InspectionForm
              onClose={handleInspectionClose}
              onSuccess={handleInspectionSuccess}
              preSelectedEstablishment={selectedEstablishmentForInspection}
            />
          </Suspense>
        </ErrorBoundary>
      ) : currentView === 'main' ? (
        <div>
          {userRole !== null && (<>
          <ErrorBoundary fallbackMessage="There was an error loading the dashboard. Please try again.">
            <Dashboard
              key={`dashboard-${refreshTrigger}-${currentView}`}
              onStatusFilterClick={handleStatusFilterClick}
              agentFilter={currentAgentFilter}
            />
          </ErrorBoundary>
          <ErrorBoundary fallbackMessage="There was an error loading the establishments list. Please try again.">
            <Establishments
              key={`establishments-${refreshTrigger}-${currentView}`}
              onNewInspection={handleNewInspection}
              onFilterChange={setEstablishmentsFilterHandler}
              onAgentFilterChange={setCurrentAgentFilter}
            />
          </ErrorBoundary>
          </>)}
        </div>
      ) : (
        <ErrorBoundary fallbackMessage="There was an error loading the dashboard. Please try again.">
          <AgentDashboard
            key={`agent-dashboard-${refreshTrigger}`}
            onNavigate={handleViewChange}
            isSuperAdmin={isSuperAdmin}
            userRole={userRole}
          />
        </ErrorBoundary>
      )}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, data: null })}
        onConfirm={confirmDialog.data?.onConfirm}
        title={confirmDialog.data?.title || 'Confirm Action'}
        message={confirmDialog.data?.message || 'Are you sure?'}
        confirmText={confirmDialog.data?.confirmText || 'Confirm'}
        cancelText="Cancel"
      />
      {showInactivityWarning && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, background: '#1a2a3e', color: '#fff',
          padding: '12px 20px', borderRadius: '10px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', gap: '14px',
          fontSize: '13px', fontFamily: "'Source Sans 3',system-ui,sans-serif",
          whiteSpace: 'nowrap'
        }}>
          <span>⏱ You'll be signed out in 5 minutes due to inactivity.</span>
          <button
            onClick={() => setShowInactivityWarning(false)}
            style={{
              background: '#3d6494', border: 'none', color: '#fff',
              padding: '5px 14px', borderRadius: '6px', cursor: 'pointer',
              fontSize: '12px', fontWeight: '600', fontFamily: 'inherit'
            }}
          >
            Stay signed in
          </button>
        </div>
      )}
    </>
  )

  return (
    <ModernLayout
      currentView={currentView}
      onNavigate={handleViewChange}
      user={user}
      onSignOut={handleSignOut}
      organization={organization}
      isSuperAdmin={isSuperAdmin}
      onSwitchOrganization={handleSwitchOrganization}
      userRole={userRole}
    >
      {renderMainContent()}
      {smsConsent === false && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
          <SMSConsentGate
            user={user}
            existingPhone={existingPhone}
            onConsent={() => { localStorage.setItem(`sms_consent_${user.id}`, '1'); setSmsConsent(true) }}
          />
        </div>
      )}
    </ModernLayout>
  )
}

function App() {
  return (
    <ErrorBoundary fallbackMessage="A critical error occurred. Please reload the page.">
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ThemeProvider>
              <LayoutProvider>
                <AppContent />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#10b981',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
              </LayoutProvider>
            </ThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
